import React, { useEffect, useRef, useState } from 'react';
import Botao from './Botao';

/** O mesmo botão serve às abas e ao "Mais". */
const BotaoAba = React.forwardRef(function BotaoAba({ className = '', children, ...resto }, ref) {
  return (
    <button ref={ref} type="button" className={['sj-aba', className].filter(Boolean).join(' ')} {...resto}>
      {children}
    </button>
  );
});

/**
 * Abas — pílulas com destaque na aba escolhida. Controlado: `valor` e `onMudar`.
 * opcoes: [{ valor, rotulo, contagem?, contagemNeutra? }]. Setas do teclado trocam de aba.
 *
 * maximo: quantas abas ficam à vista; as outras vão para "Mais", que mostra o nome
 *   da aba escolhida quando ela está lá dentro (ficha no celular: 2; no computador: 5).
 * fundo: a aba escolhida ganha fundo (sj-abas--sem-indicador), como nas fichas.
 * contagemNeutra: a contagem é só um número (Processos: 2), não um alerta vermelho.
 */
export default function Abas({ opcoes, valor, onMudar, rotulo = 'Abas', maximo, fundo = false }) {
  const [maisAberto, setMaisAberto] = useState(false);
  const raizMais = useRef(null);
  const gatilhoMais = useRef(null);

  const corta = Boolean(maximo) && opcoes.length > maximo;
  const visiveis = corta ? opcoes.slice(0, maximo) : opcoes;
  const extras = corta ? opcoes.slice(maximo) : [];
  const extraAtual = extras.find((o) => o.valor === valor);
  const algumaVisivelAtiva = visiveis.some((o) => o.valor === valor);

  useEffect(() => {
    if (!maisAberto) return undefined;
    const aoClicarFora = (e) => { if (!raizMais.current?.contains(e.target)) setMaisAberto(false); };
    const aoApertarEsc = (e) => {
      if (e.key !== 'Escape') return;
      setMaisAberto(false);
      gatilhoMais.current?.focus();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoApertarEsc);
    raizMais.current?.querySelector('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoApertarEsc);
    };
  }, [maisAberto]);

  const aoTeclar = (e, indice) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const passo = e.key === 'ArrowRight' ? 1 : -1;
    const proximo = visiveis[(indice + passo + visiveis.length) % visiveis.length];
    onMudar?.(proximo.valor);
    document.getElementById(`aba-${proximo.valor}`)?.focus();
  };

  const escolherExtra = (opcao) => {
    setMaisAberto(false);
    onMudar?.(opcao.valor);
    gatilhoMais.current?.focus();
  };

  return (
    <div className={['sj-abas', fundo ? 'sj-abas--sem-indicador' : ''].filter(Boolean).join(' ')}>
      <div className="sj-abas__lista" role="tablist" aria-label={rotulo}>
        {visiveis.map((opcao, indice) => {
          const ativa = opcao.valor === valor;
          const focavel = ativa || (!algumaVisivelAtiva && indice === 0);
          return (
            <BotaoAba
              key={opcao.valor}
              id={`aba-${opcao.valor}`}
              role="tab"
              aria-selected={ativa}
              tabIndex={focavel ? 0 : -1}
              onClick={() => onMudar?.(opcao.valor)}
              onKeyDown={(e) => aoTeclar(e, indice)}
            >
              {opcao.rotulo}
              {opcao.contagem != null && (
                <span className={['sj-aba__contagem', opcao.contagemNeutra ? 'sj-aba__contagem--neutra' : ''].filter(Boolean).join(' ')}>
                  {opcao.contagem}
                </span>
              )}
            </BotaoAba>
          );
        })}
      </div>
      {corta && (
        <div ref={raizMais} className="sj-abas__mais">
          <BotaoAba
            ref={gatilhoMais}
            className={extraAtual ? 'sj-aba--atual' : ''}
            aria-haspopup="menu"
            aria-expanded={maisAberto}
            onClick={() => setMaisAberto((v) => !v)}
          >
            {extraAtual ? extraAtual.rotulo : 'Mais'}
            <span className="sj-icone sj-icone--sm" aria-hidden="true">expand_more</span>
          </BotaoAba>
          {maisAberto && (
            <div className="sj-lista sj-lista--direita" role="menu" aria-label={`Mais: ${rotulo}`}>
              {extras.map((opcao) => (
                <Botao key={opcao.valor} item role="menuitem" onClick={() => escolherExtra(opcao)}>
                  {opcao.rotulo}
                </Botao>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
